import { useCallback, useEffect, useState } from "react";
import { Loader2, Plane, Plus, RefreshCw, Trash2, Palmtree } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { useHub } from "@/components/AppShell";
import { EmptyState, Field, Loader, PageHeader } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdultAvatar, KidAvatar } from "@/components/Avatars";
import { BLUE, parseYmd } from "@/lib/calendar";

const t12 = (hm) => { const [h, m] = hm.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const fdate = (d) => parseYmd(d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });

function Travellers({ keys, people, tid }) {
  const by = Object.fromEntries(people.map((p) => [p.key, p]));
  return (
    <div className="flex -space-x-2" data-testid={`flight-travellers-${tid}`}>
      {keys.map((k) => by[k]).filter(Boolean).map((p) => <span key={p.key} title={p.name} className="rounded-full ring-2 ring-q-surface">{p.kind === "kid" ? <KidAvatar avatar={p.avatar} size={30} /> : <AdultAvatar name={p.name} picture={p.picture} size={30} />}</span>)}
    </div>
  );
}

function FlightCard({ f, people, canEdit, live, onChanged }) {
  const tid = f.flight_no.replace(" ", "").toLowerCase() + "-" + f.date;
  const [busy, setBusy] = useState(false);
  const refresh = async () => { setBusy(true); try { await api.post(`/organizations/${f.organization_id}/flights/${f.id}/refresh`); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const del = async () => { try { await api.delete(`/organizations/${f.organization_id}/flights/${f.id}`); toast.success("Flight removed"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="q-card flex flex-col gap-3 q-rise" data-testid={`flight-card-${tid}`}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(var(--st-sky),0.14)" }}><Plane className="h-5 w-5" style={{ color: "rgb(var(--st-sky))" }} /></span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-extrabold text-q-text" data-testid={`flight-number-${tid}`}>{f.flight_no} · {f.from_airport} → {f.to_airport}</p>
          <p className="text-sm text-q-muted">{fdate(f.date)} · {t12(f.dep_time)} → {t12(f.arr_time)}{f.arr_next_day ? " (+1 day)" : ""} <span className="text-q-faint">local times</span></p>
        </div>
        <Travellers keys={f.travellers} people={people} tid={tid} />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="rounded-full bg-q-raised px-3 py-1 font-semibold text-q-muted" data-testid={`flight-status-${tid}`}>{f.live.label}</span>
        {f.confirmation && <span className="rounded-full border border-q-line px-3 py-1 font-mono text-xs font-bold text-q-text" data-testid={`flight-confirmation-${tid}`}>Confirmation {f.confirmation}</span>}
        {f.notes && <span className="text-xs text-q-faint">{f.notes}</span>}
        <span className="flex-1" />
        {live && <button className="q-btn q-btn-ghost min-h-[44px]" disabled={busy} onClick={refresh} data-testid={`flight-refresh-${tid}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh status</button>}
        {canEdit && <button className="q-btn q-btn-ghost h-11 w-11 p-0" onClick={del} aria-label="Remove flight" data-testid={`flight-delete-${tid}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}

const EMPTY = { flight_no: "", date: "", from_airport: "", to_airport: "", dep_time: "", arr_time: "", arr_next_day: false, travellers: [], confirmation: "", notes: "" };

function validate(f) {
  const e = {};
  if (!/^[A-Z0-9]{2}\s?\d{1,4}$/i.test(f.flight_no.trim())) e.flight_no = "Enter the airline and number, like DL 1234";
  if (!f.date) e.date = "Pick the departure date";
  if (!/^[A-Z]{3}$/i.test(f.from_airport.trim())) e.from_airport = "Enter a 3-letter airport code, like ATL";
  if (!/^[A-Z]{3}$/i.test(f.to_airport.trim())) e.to_airport = "Enter a 3-letter airport code, like MCO";
  else if (f.to_airport.trim().toUpperCase() === f.from_airport.trim().toUpperCase()) e.to_airport = "Arrival must be a different airport";
  if (!f.dep_time || !f.arr_time) e.dep_time = "Add the departure and arrival times";
  return e;
}

function AddFlight({ hubId, people, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { const v = e.target.type === "checkbox" ? e.target.checked : e.target.value; setF((p) => ({ ...p, [k]: v })); setErrs({}); };
  const save = async (e) => {
    e.preventDefault();
    const v = validate(f);
    if (Object.keys(v).length) return setErrs(v);
    setBusy(true);
    try { await api.post(`/organizations/${hubId}/flights`, f); toast.success(`Flight ${f.flight_no.toUpperCase()} added to the calendar`); onSaved(); } catch (x) {
      const d = x?.response?.data?.detail;
      if (d?.field) setErrs({ [d.field]: d.message }); else toast.error(errMsg(x));
    } finally { setBusy(false); }
  };
  const input = (k, label, props = {}) => (
    <Field label={label} htmlFor={`fl-${k}`} error={errs[k]} errorTestid={`flight-${k}-error`}>
      <input id={`fl-${k}`} className="q-input" value={f[k]} onChange={set(k)} aria-invalid={!!errs[k]} data-testid={`flight-${k}-input`} {...props} />
    </Field>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="flight-form">
        <DialogHeader><DialogTitle>Add a flight</DialogTitle></DialogHeader>
        <form className="space-y-4" noValidate onSubmit={save}>
          <div className="grid grid-cols-2 gap-3">{input("flight_no", "Airline + flight number", { placeholder: "DL 1234" })}{input("date", "Departure date", { type: "date" })}</div>
          <div className="grid grid-cols-2 gap-3">{input("from_airport", "From (airport)", { placeholder: "ATL", maxLength: 3 })}{input("to_airport", "To (airport)", { placeholder: "MCO", maxLength: 3 })}</div>
          <div className="grid grid-cols-2 gap-3">{input("dep_time", "Departs (local)", { type: "time" })}{input("arr_time", "Arrives (local)", { type: "time" })}</div>
          <label className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-q-text"><input type="checkbox" className="h-5 w-5" checked={f.arr_next_day} onChange={set("arr_next_day")} data-testid="flight-next-day-checkbox" /> Arrives the next day</label>
          <div><p className="q-label">Travellers</p><div className="flex flex-wrap gap-2">
            {people.map((p) => { const on = f.travellers.includes(p.key); return <button type="button" key={p.key} aria-pressed={on} data-testid={`flight-traveller-${p.name.split(" ")[0].toLowerCase()}`}
              onClick={() => setF((x) => ({ ...x, travellers: on ? x.travellers.filter((k) => k !== p.key) : [...x.travellers, p.key] }))}
              className={`min-h-[44px] rounded-full border px-3 text-sm font-semibold ${on ? "border-transparent text-white" : "border-q-line text-q-muted"}`} style={on ? { background: `rgb(${BLUE})` } : undefined}>{p.name.split(" ")[0]}</button>; })}
          </div></div>
          <div className="grid grid-cols-2 gap-3">{input("confirmation", "Confirmation code (adults only)")}{input("notes", "Notes")}</div>
          <div className="flex justify-end gap-2">
            <button type="button" className="q-btn q-btn-secondary" onClick={onClose} data-testid="flight-cancel-btn">Cancel</button>
            <button className="q-btn q-btn-primary" disabled={busy} data-testid="flight-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Add flight</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Vacation({ v, people }) {
  return (
    <div className="q-card flex items-center gap-3" data-testid={`trip-vacation-${v.id}`}>
      <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(var(--st-pink),0.14)" }}><Palmtree className="h-5 w-5" style={{ color: "rgb(var(--st-pink))" }} /></span>
      <div className="min-w-0 flex-1"><p className="font-display font-extrabold text-q-text">{v.title}</p><p className="text-sm text-q-muted">{fdate(v.start.slice(0, 10))} – {fdate(v.end.slice(0, 10))}</p></div>
      <Travellers keys={v.member_keys || []} people={people} tid={`vacation-${v.id}`} />
    </div>
  );
}

export default function Flights() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [adding, setAdding] = useState(false);
  const [past, setPast] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/flights`).then(({ data }) => setD(data)), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.organization_id === hub.id && m.entity === "calendar.event");
  if (!d) return <Loader />;
  const card = (f) => <FlightCard key={f.id} f={f} people={d.people} canEdit={d.can_edit} live={d.live_status} onChanged={load} />;
  return (
    <div data-testid="flights-page">
      <PageHeader eyebrow="Calendar" title="Flights & Trips" rgb={BLUE} testid="flights-title"
        actions={d.can_edit && <button className="q-btn q-btn-primary" onClick={() => setAdding(true)} data-testid="flight-add-btn"><Plus className="h-4 w-4" /> Add flight</button>} />
      {!d.live_status && <p className="mb-4 text-sm text-q-faint" data-testid="flights-live-off-note">Live status is off. Flight times show as you entered them.</p>}
      <div className="space-y-3" data-testid="flights-upcoming">
        {d.upcoming.length || d.vacations.upcoming.length ? <>{d.vacations.upcoming.map((v) => <Vacation key={v.id} v={v} people={d.people} />)}{d.upcoming.map(card)}</>
          : <EmptyState Icon={Plane} rgb={BLUE} title="No trips coming up" hint="Add a flight and it shows on the family calendar." testid="flights-empty" />}
      </div>
      <button className="q-btn q-btn-ghost mt-6 min-h-[44px]" onClick={() => setPast((p) => !p)} aria-expanded={past} data-testid="flights-past-toggle">{past ? "Hide past trips" : "Past trips"}</button>
      {past && <div className="mt-3 space-y-3 opacity-80" data-testid="flights-past">{d.past.length || d.vacations.past.length ? <>{d.vacations.past.map((v) => <Vacation key={v.id} v={v} people={d.people} />)}{d.past.map(card)}</> : <p className="text-sm text-q-muted">No past trips yet.</p>}</div>}
      {adding && <AddFlight hubId={hub.id} people={d.people} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}
